import createRequest from './portalApiClient';
import { env } from '../../config/env';

/**
 * Geocoding API Client
 *
 * Centralized API client for geocoding operations (address → coordinates, coordinates → address).
 * Part of Store Location Editor feature.
 */

// Unwrap API response to extract data
const unwrap = <T,>(payload: any): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data as T;
  }
  return payload as T;
};

// Request instance
const request = createRequest(process.env.REACT_APP_API_BASE_URL || env.apiBase || '');

// Types
export interface GeocodeAddressRequest {
  address: string;
  pincode?: string;
}

export interface GeocodeAddressResponse {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  pincode?: string;
}

export interface ReverseGeocodeRequest {
  latitude: number;
  longitude: number;
}

export interface ReverseGeocodeResponse {
  address: string;
  pincode?: string;
}

export const geocodingApi = {
  /**
   * Convert address string to coordinates
   *
   * @param address - Address string to geocode
   * @param pincode - Optional pincode for validation
   * @returns Coordinates and formatted address
   *
   * @example
   * const result = await geocodingApi.geocodeAddress('Shop 23, Road 2, Boduppal, Hyderabad', '500039');
   * console.log(result.latitude, result.longitude);
   */
  async geocodeAddress(address: string, pincode?: string): Promise<GeocodeAddressResponse> {
    const payload = await request<any>('/v1/geocode/address', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ address, pincode })
    });
    return unwrap<GeocodeAddressResponse>(payload);
  },

  /**
   * Convert coordinates to address (reverse geocoding)
   *
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @returns Address and pincode
   *
   * @example
   * const result = await geocodingApi.reverseGeocode(17.421268, 78.588932);
   * console.log(result.address, result.pincode);
   */
  async reverseGeocode(latitude: number, longitude: number): Promise<ReverseGeocodeResponse> {
    const payload = await request<any>('/v1/geocode/reverse', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ latitude, longitude })
    });
    return unwrap<ReverseGeocodeResponse>(payload);
  }
};
